// /components/new: the new-widget wizard.
//   1. Component type (ASHP Estimator, Solar estimate or Contact form), then
//      its name and style (Compact / Full width / Hosted; a solar estimate is
//      Full width or Hosted only, its map needs room) → create
//   2. The estimate method terms, accepted for this widget (solar: its own terms)
//   3. Where enquiries are emailed: every address must be confirmed before the
//      wizard carries on (your own sign-in address already is)
//   4. Prices: at least one heat pump size switched on with a price, or for a
//      solar estimate a base price, a price per panel and at least one inverter price
//   5. Websites it may run on: at least one (not for hosted pages, which run on go.<domain>)
// …then the widget's Branding tab (then Pricing, Install). A pulsing star marks
// the next thing to do. The step after creation is in the URL (?w=&step=).
// The type chosen on page 1 is ?kind=ashp|solar|contact; a solar estimate is
// created with product "solar" (fixed from then on, docs/12-solar-roof-scan.md §9).
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { ProductIcon } from "../../components/ProductIcon";
import {
  CONTACT_COPY,
  DEFAULT_BRANDING,
  DEFAULT_SOLAR_PRICING,
  SOLAR_COPY,
  SOLAR_DEFAULT_HEIGHT,
  SOLAR_LAUNCHER_LABEL,
  hasPrices,
  normalisePricing,
  type WidgetBranding,
  type WidgetProduct,
  type WidgetStyle,
} from "@widget";

import { useApi } from "../../api";
import { useAuth } from "../../auth/AuthContext";
import { Attention } from "../../components/Attention";
import { DomainsEditor } from "../../components/DomainsEditor";
import { EMAIL_CONTENTS, SOLAR_EMAIL_CONTENTS, MAX_EMAILS, NotifyEmailsEditor, useRecipients } from "../../components/NotifyEmailsEditor";
import { PriceEditor, priceProblems, toPriceState, type PriceState } from "../../components/PriceEditor";
import {
  SOLAR_PRICE_EXPLAINER,
  SolarPriceEditor,
  SolarPriceExample,
  fromSolarForm,
  hasSolarPrices,
  solarProblems,
  toSolarForm,
  type SolarForm,
} from "../../components/SolarPriceEditor";
import { TermsCard } from "../../components/TermsCard";
import "../../widgets.css";
import { track } from "../../track";

/**
 * Branding for a new widget of each style. Full width always sits in the page,
 * full width. A contact form is the compact card with its own wording. A solar
 * estimate has its own wording and launcher label (SOLAR_COPY) and is only ever
 * Full width or Hosted (anything else becomes Full width, as the API does).
 */
export function brandingFor(style: WidgetStyle, product: WidgetProduct = "ashp"): WidgetBranding {
  if (product === "solar") {
    const solarStyle: WidgetStyle = style === "hosted" ? "hosted" : "full";
    return {
      ...DEFAULT_BRANDING,
      copy: SOLAR_COPY,
      layout: { ...DEFAULT_BRANDING.layout, style: solarStyle, mode: "inline", fullWidth: true, launcherLabel: SOLAR_LAUNCHER_LABEL, height: SOLAR_DEFAULT_HEIGHT },
    };
  }
  if (style === "contact") {
    return { ...DEFAULT_BRANDING, copy: CONTACT_COPY, layout: { ...DEFAULT_BRANDING.layout, style: "contact", launcherLabel: "Contact us" } };
  }
  return style === "small"
    ? { ...DEFAULT_BRANDING, layout: { ...DEFAULT_BRANDING.layout, style: "small" } }
    : { ...DEFAULT_BRANDING, layout: { ...DEFAULT_BRANDING.layout, style, mode: "inline", fullWidth: true } };
}

type WizardStep = "type" | "setup" | "terms" | "notify" | "pricing" | "websites";
const WIZARD_STEPS: { key: WizardStep | "branding"; label: string }[] = [
  { key: "type", label: "Component type" },
  { key: "setup", label: "Name and style" },
  { key: "terms", label: "How estimates work" },
  { key: "notify", label: "Notifications" },
  { key: "pricing", label: "Prices" },
  { key: "websites", label: "Websites" },
  { key: "branding", label: "Branding and install" },
];

const CHOICES: { style: WidgetStyle; title: string; body: string; image: string }[] = [
  {
    style: "small",
    title: "Compact",
    body: "A neat card for a sidebar or section of an existing page, or a floating button on every page. Quick questions, instant price.",
    image: "/wizard/compact.jpg",
  },
  {
    style: "full",
    title: "Full width",
    body: "A full quote page: the home's EPC, every property detail, a grant check and the heat loss breakdown. Best on its own page.",
    image: "/wizard/full.jpg",
  },
  {
    style: "hosted",
    title: "Hosted page",
    body: "The full width calculator on a page we host for you, with your own header and footer. Send people a link or a QR code: no website needed.",
    image: "/wizard/hosted.jpg",
  },
];

// A solar estimate's styles: no Compact (the satellite map needs room, docs/12 §7.1)
const SOLAR_CHOICES: typeof CHOICES = [
  {
    style: "full",
    title: "Full width",
    body: "The roof scan on a page of your website: a satellite map of the home, panels laid on its roof and your price. Best on its own page.",
    image: "/wizard/full.jpg",
  },
  {
    style: "hosted",
    title: "Hosted page",
    body: "The roof scan on a page we host for you, with your own header and footer. Send people a link or a QR code: no website needed.",
    image: "/wizard/hosted.jpg",
  },
];


type Kind = "ashp" | "solar" | "contact";
const KINDS: Kind[] = ["ashp", "solar", "contact"];

export function NewWidgetPage() {
  const api = useApi();
  const navigate = useNavigate();
  // Where the wizard is, kept in the URL so a refresh resumes it (?w=<widgetId>&step=terms|notify|pricing|websites)
  const [params, setParams] = useSearchParams();
  const widgetId = params.get("w");
  const kind: Kind | null = KINDS.find((k) => k === params.get("kind")) ?? null;
  const urlStep = params.get("step");
  const step: WizardStep = widgetId ? (urlStep === "notify" || urlStep === "pricing" || urlStep === "websites" ? urlStep : "terms") : kind ? "setup" : "type";

  // The type chosen on page 1 (it moves to the URL on Next); starts as the one in the URL
  const [picked, setPicked] = useState<Kind | null>(kind);
  const [name, setName] = useState("");
  const [style, setStyle] = useState<WidgetStyle | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Starts with your own sign-in address: Cognito verified it, so it's confirmed already
  const { user } = useAuth();
  const [emails, setEmails] = useState<string[]>(() => (user?.email ? [user.email.toLowerCase()] : []));
  const [saving, setSaving] = useState(false);
  // Which addresses are confirmed (re-checked while any is waiting)
  const recipients = useRecipients(emails);
  // Step 5: prices (all sizes start switched off; at least one must be priced)
  const [prices, setPrices] = useState<PriceState>(() => toPriceState(normalisePricing(null)));
  const priceIssues = priceProblems(prices);
  const pricesReady = priceIssues === 0 && hasPrices(prices.pricing);
  // Step 5 for a solar estimate: system and inverter prices (start from the example
  // prices; base, per panel and at least one inverter must have a price)
  const [solarForm, setSolarForm] = useState<SolarForm>(() => toSolarForm(DEFAULT_SOLAR_PRICING));
  const solarIssues = solarProblems(solarForm).count;
  const solarReady = solarIssues === 0 && hasSolarPrices(solarForm);

  // Paid-for extras: a Hosted page needs single page hosting on its LeadFlow
  // (ASHP or Solar; QuoteFlow includes both), a Contact form the contact forms
  // add-on (or a legacy line that includes them).
  // Without one, its card is greyed and points to Billing. Unknown (loading or
  // a failed check) leaves both open; the component's status would say it's unpaid.
  const [allowed, setAllowed] = useState<{ hosted: boolean; solarHosted: boolean; contact: boolean } | null>(null);
  useEffect(() => {
    api
      .getBilling()
      .then((b) =>
        setAllowed({
          hosted: Boolean(b.demo) || (b.limits?.hosted ?? 0) > 0,
          solarHosted: Boolean(b.demo) || (b.limits?.solarhosted ?? 0) > 0,
          contact: Boolean(b.demo) || (b.limits?.contact ?? 0) > 0,
        }),
      )
      .catch(() => setAllowed(null));
  }, [api]);
  // A hosted solar page needs LeadFlow Solar's hosting, a heat pump one LeadFlow ASHP's
  const hostedLocked = (kind === "solar" ? allowed?.solarHosted : allowed?.hosted) === false;
  const contactLocked = allowed?.contact === false;

  // Step 6: the websites it may run on (skipped for hosted pages). The tool's
  // style is read back from the API so a refreshed wizard still knows it.
  const [domains, setDomains] = useState<string[]>([]);
  const [toolStyle, setToolStyle] = useState<WidgetStyle | null>(null);
  // The product is read back too, so a refreshed solar wizard keeps its prices step
  const [toolProduct, setToolProduct] = useState<WidgetProduct | null>(null);
  useEffect(() => {
    if (!widgetId) return;
    api
      .getWidget(widgetId)
      .then((w) => {
        setToolStyle(w.branding?.layout.style ?? "small");
        setToolProduct(w.product ?? "ashp");
      })
      .catch(() => undefined);
  }, [api, widgetId]);
  const hostedTool = (toolStyle ?? style) === "hosted";
  // A contact form gives no estimates: no estimate terms or prices to set up
  const contactForm = (toolStyle ?? (kind === "contact" ? "contact" : style)) === "contact";
  // A solar estimate: its own terms and system prices (falls back to ?kind while loading)
  const solar = (toolProduct ?? (kind === "solar" ? "solar" : "ashp")) === "solar";
  const steps = WIZARD_STEPS.filter((s) => !(hostedTool && s.key === "websites") && !(contactForm && (s.key === "terms" || s.key === "pricing"))).map(
    (s) => (solar && s.key === "pricing" ? { ...s, label: "Your prices" } : s),
  );
  // Styles offered on the name/style page (solar: Full width and Hosted only)
  const choices = kind === "solar" ? SOLAR_CHOICES : CHOICES;

  // Where the pulsing star sits on the name/style page: name → style → create
  const next = !name.trim() ? "name" : !style && kind !== "contact" ? "style" : "create";

  async function create(e: FormEvent) {
    e.preventDefault();
    const chosen: WidgetStyle | null = kind === "contact" ? "contact" : style;
    if (!name.trim() || !chosen) return;
    // Only solar estimates are created as "solar"; contact forms stay "ashp" (no prices)
    const product: WidgetProduct = kind === "solar" ? "solar" : "ashp";
    setCreating(true);
    setError(null);
    try {
      const w = await api.createWidget(name.trim(), product);
      track("tool_created", { style: chosen, product });
      // Save the chosen style straight away, so Branding opens with it (solar: its own wording)
      await api.updateWidget(w.widgetId, { branding: brandingFor(chosen, product) });
      // A contact form has no estimate terms: straight to where enquiries go
      setParams({ w: w.widgetId, step: chosen === "contact" ? "notify" : "terms" });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function saveEmails() {
    // Enquiries only go to confirmed addresses, so the wizard can't finish without them
    if (!widgetId || !recipients.allConfirmed) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateWidget(widgetId, { notifyEmails: emails });
      // A contact form has no prices: straight to its websites
      setParams({ w: widgetId, step: contactForm ? "websites" : "pricing" });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function savePrices() {
    // A tool can't quote without at least one priced size (solar: base, per panel and an inverter)
    if (!widgetId || !(solar ? solarReady : pricesReady)) return;
    setSaving(true);
    setError(null);
    try {
      const w = await api.updateWidget(widgetId, solar ? { solarPricing: fromSolarForm(solarForm) } : { pricing: prices.pricing });
      // Hosted pages run on our own page: no websites to allow, straight to branding
      if (w.branding?.layout.style === "hosted") navigate(`/components/${widgetId}/branding`);
      else {
        setParams({ w: widgetId, step: "websites" });
        setSaving(false);
      }
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  }

  async function saveDomains() {
    // The calculator won't run anywhere without at least one allowed website
    if (!widgetId || domains.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateWidget(widgetId, { domains });
      navigate(`/components/${widgetId}/branding`);
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>New website component</h1>
        <p>A few steps to set it up. After these you'll style it and install it.</p>
      </div>

      {/* Where the installer is in the wizard */}
      <ol className="wiz-steps" aria-label="Steps">
        {steps.map((s, i) => {
          const at = steps.findIndex((x) => x.key === step);
          const state = i < at ? "done" : i === at ? "current" : "todo";
          return (
            <li key={s.key} className={`wiz-step is-${state}`} aria-current={state === "current" ? "step" : undefined}>
              <span className="wiz-step-num">{state === "done" ? <Check size={14} aria-hidden="true" /> : i + 1}</span>
              {s.label}
            </li>
          );
        })}
      </ol>

      {error && <div className="flash flash-danger" role="alert">{error}</div>}

      {/* Page 1: widget type */}
      {step === "type" && (
        <div className="wiz">
          {/* What kind of widget: pick one, then Next */}
            <div className="wiz-kinds" role="radiogroup" aria-label="Component type">
              <button type="button" role="radio" aria-checked={picked === "ashp"} className={`card wiz-kind has-attention${picked === "ashp" ? " is-on" : ""}`} onClick={() => setPicked("ashp")}>
                <span className="wiz-kind-icon">
                  <ProductIcon product="ashp" size={44} />
                </span>
                <span>
                  <span className="wiz-choice-title">ASHP Estimator</span>
                  <span className="wiz-choice-body">Homeowners get an air source heat pump size and price estimate in seconds from their home’s EPC, and (with QuoteFlow) a detailed quote.</span>
                </span>
              </button>
              {/* Solar estimate (docs/12-solar-roof-scan.md): a roof scan on a satellite map */}
              <button type="button" role="radio" aria-checked={picked === "solar"} className={`card wiz-kind${picked === "solar" ? " is-on" : ""}`} onClick={() => setPicked("solar")}>
                <span className="wiz-kind-icon">
                  <ProductIcon product="solar" size={44} />
                </span>
                <span>
                  <span className="wiz-choice-title">Solar estimate</span>
                  <span className="wiz-choice-body">Homeowners see panels on their own roof and your price.</span>
                  {/* Detailed solar quotes are not built yet: say so, so installers know it's on the way */}
                  <span className="wiz-choice-soon">QuoteFlow detailed quotes for solar: planned for November 2026</span>
                </span>
              </button>
              {contactLocked ? (
                // No contact forms add-on: a greyed card (not a choice) with the way to add it
                <div className="card wiz-kind is-locked" aria-disabled="true">
                  <span className="wiz-kind-icon">
                    <ProductIcon product="contact" size={44} />
                  </span>
                  <span>
                    <span className="wiz-choice-title">Contact form</span>
                    <span className="wiz-choice-body">A simple enquiry form for any page, emailed straight to you. Optionally ask for the address and what the enquiry is about.</span>
                    <span className="wiz-choice-lock">
                      Needs the Contact forms add-on. <Link to="/billing">Add it in Billing</Link>
                    </span>
                  </span>
                </div>
              ) : (
                <button type="button" role="radio" aria-checked={picked === "contact"} className={`card wiz-kind${picked === "contact" ? " is-on" : ""}`} onClick={() => setPicked("contact")}>
                  <span className="wiz-kind-icon">
                    <ProductIcon product="contact" size={44} />
                  </span>
                  <span>
                    <span className="wiz-choice-title">Contact form</span>
                    <span className="wiz-choice-body">A simple enquiry form for any page, emailed straight to you. Optionally ask for the address and what the enquiry is about.</span>
                  </span>
                </button>
              )}
              {/* Single Page Website: hidden until it is ready */}
            </div>
          <div className="wiz-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate("/components")}>
              <ArrowLeft size={16} aria-hidden="true" /> Back
            </button>
            <span className="has-attention">
              <button type="button" className="btn btn-primary" disabled={!picked} onClick={() => picked && setParams({ kind: picked })}>
                Next <ArrowRight size={16} aria-hidden="true" />
              </button>
              {picked && <Attention corner="tr" label={picked === "contact" ? "Next: name it" : "Next: name and style"} />}
            </span>
          </div>
        </div>
      )}

      {step === "setup" && (
        <form onSubmit={create} className="wiz">
          <>
              {/* Name */}
              <div className="card wiz-name">
                <label htmlFor="new-widget-name" className="new-widget-label">
                  {kind === "contact" ? "Contact form name" : kind === "solar" ? "Solar estimate name" : "ASHP Estimator name"}
                </label>
                <span className="has-attention wiz-name-field">
                  <input
                    id="new-widget-name"
                    className="input"
                    placeholder="e.g. Main website, Landing page, Partner site"
                    value={name}
                    maxLength={60}
                    autoFocus
                    onChange={(e) => setName(e.target.value)}
                    disabled={creating}
                  />
                  {next === "name" && <Attention corner="tr" label={kind === "contact" ? "Name your form" : "Name your calculator"} />}
                </span>
                <p className="hosted-hint">Only you see this: it tells your website components apart in the console.</p>
              </div>

              {/* How it looks (calculators only: a contact form is the compact card) */}
              {kind !== "contact" && (
              <div className="wiz-choices has-attention" role="radiogroup" aria-label="Calculator style">
                {next === "style" && <Attention corner="tl" label="Choose how it looks" />}
                {choices.map((c) =>
                  // Hosted page without its LeadFlow's hosting: a greyed card (not a
                  // choice) with the way to add it
                  c.style === "hosted" && hostedLocked ? (
                    <div key={c.style} className="card wiz-choice is-locked" aria-disabled="true">
                      <img className="wiz-shot" src={c.image} alt="" loading="lazy" />
                      <span className="wiz-choice-title">{c.title}</span>
                      <span className="wiz-choice-body">{c.body}</span>
                      <span className="wiz-choice-lock">
                        Needs single page hosting on LeadFlow {kind === "solar" ? "Solar" : "ASHP"}. <Link to="/billing">Add it in Billing</Link>
                      </span>
                    </div>
                  ) : (
                    <button
                      key={c.style}
                      type="button"
                      role="radio"
                      aria-checked={style === c.style}
                      className={`card wiz-choice${style === c.style ? " is-on" : ""}`}
                      onClick={() => setStyle(c.style)}
                    >
                      {/* A picture of that style (static: quick to load, no widget running) */}
                      <img className="wiz-shot" src={c.image} alt="" loading="lazy" />
                      <span className="wiz-choice-title">{c.title}</span>
                      <span className="wiz-choice-body">{c.body}</span>
                    </button>
                  ),
                )}
              </div>
              )}
          </>

          <div className="wiz-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setParams({})} disabled={creating}>
              <ArrowLeft size={16} aria-hidden="true" /> Back
            </button>
            <span className="has-attention">
              <button type="submit" className="btn btn-primary" disabled={creating || !name.trim() || (!style && kind !== "contact")}>
                {creating ? "Creating…" : "Create and style it"} <ArrowRight size={16} aria-hidden="true" />
              </button>
              {next === "create" && !creating && <Attention corner="tr" label={kind === "contact" ? "Create your form" : "Create your calculator"} />}
            </span>
          </div>
        </form>
      )}

      {/* Step 2: the estimate method, accepted for this widget */}
      {step === "terms" && widgetId && (
        <TermsCard
          widgetId={widgetId}
          guide
          // A solar estimate accepts its own terms (the solar method statement)
          product={solar ? "solar" : "ashp"}
          intro="Before homeowners see any estimates, please read how the calculator works them out and accept it for this calculator. You can read this again on the Install tab."
          buttonText="Accept and continue"
          onAccepted={() => setParams({ w: widgetId, step: "notify" })}
        />
      )}

      {/* Step 3: where enquiries go */}
      {step === "notify" && widgetId && (
        <div className="wiz">
          <section className="card">
            <h2>Where should enquiries go?</h2>
            <p className="card-sub">
              {contactForm
                ? <>When someone sends the form, their message is <strong>emailed</strong> to these addresses straight away, with their contact details (and the address and topic, if you ask for them).</>
                : <>When a homeowner asks for a callback, their enquiry is <strong>emailed</strong> to these addresses straight away: their contact details,
              the property, the estimate and the price they were shown.</>} We don't keep a copy (the email is the only record), so use an inbox your
              team checks. Up to {MAX_EMAILS} addresses; you can change them later on the Notifications tab.
            </p>
            {/* Why addresses must be confirmed, and how */}
            <div className="notify-explain">
              <p>
                <strong>Every address must be confirmed before you carry on.</strong> Enquiries hold homeowners' personal details, so we only send
                them to inboxes that have said yes.
              </p>
              <ul>
                <li>Your own sign-in address{user?.email ? <> (<strong>{user.email}</strong>)</> : null} is confirmed already.</li>
                <li>Any address you've confirmed before, for any of your components, is accepted straight away.</li>
                <li>
                  Any other address gets an email with a <strong>Confirm this address</strong> button. Once it's clicked, this page updates by
                  itself. Nothing is sent to it until then.
                </li>
              </ul>
            </div>
            <NotifyEmailsEditor emails={emails} onChange={setEmails} recipients={recipients} guide />
          </section>
          {!contactForm && (
          <section className="card">
            <h2>What each email contains</h2>
            <ul className="notify-contents">
              {(solar ? SOLAR_EMAIL_CONTENTS : EMAIL_CONTENTS).map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </section>
          )}
          <div className="wiz-actions wiz-actions-end">
            {/* Why the button is still off: waiting for someone to click their link */}
            {emails.length > 0 && !recipients.allConfirmed && (
              <span className="wiz-wait">Waiting for every address to be confirmed…</span>
            )}
            <span className="has-attention">
              <button type="button" className="btn btn-primary" onClick={() => void saveEmails()} disabled={saving || !recipients.allConfirmed}>
                {saving ? "Saving…" : contactForm ? "Save and add websites" : solar ? "Save and set your prices" : "Save and set prices"} <ArrowRight size={16} aria-hidden="true" />
              </button>
              {recipients.allConfirmed && !saving && <Attention corner="tr" label="Save and continue" />}
            </span>
          </div>
        </div>
      )}

      {/* Step 5 (solar): system prices and inverter steps, with a worked example */}
      {step === "pricing" && widgetId && solar && (
        <div className="wiz">
          <section className="card pricing">
            <h2>Your prices</h2>
            <p className="card-sub">
              A base price for the installation, a price per panel and the inverter you fit for each system size, all{" "}
              <strong>including installation and VAT</strong>. These start as example prices: change them to yours. You can change them later
              on the Pricing tab.
            </p>
            {solarIssues > 0 && (
              <div className="flash flash-danger">
                {solarIssues === 1 ? "1 field needs attention" : `${solarIssues} fields need attention`} before you can carry on.
              </div>
            )}
            <SolarPriceEditor form={solarForm} onChange={setSolarForm} />
          </section>
          {/* What homeowners see with these prices */}
          <section className="card pricing-explain">
            <h2>How the price is worked out</h2>
            <p className="card-sub">{SOLAR_PRICE_EXPLAINER}</p>
            <SolarPriceExample form={solarForm} />
          </section>
          <div className="wiz-actions wiz-actions-end">
            {!solarReady && solarIssues === 0 && <span className="wiz-wait">Set a base price, a price per panel and at least one inverter price.</span>}
            <span className="has-attention">
              <button type="button" className="btn btn-primary" onClick={() => void savePrices()} disabled={saving || !solarReady}>
                {saving ? "Saving…" : hostedTool ? "Save and go to branding" : "Save and add websites"} <ArrowRight size={16} aria-hidden="true" />
              </button>
              {solarReady && !saving && <Attention corner="tr" label="Save and continue" />}
            </span>
          </div>
        </div>
      )}

      {/* Step 5: at least one heat pump size and its price */}
      {step === "pricing" && widgetId && !solar && (
        <div className="wiz">
          <section className="card pricing">
            <h2>Your prices</h2>
            <p className="card-sub">
              Switch on the heat pump sizes you install and enter your full installed price for each, <strong>before</strong> the Boiler
              Upgrade Scheme grant. <strong>You need at least one</strong>: the calculator picks the smallest size you offer that covers the
              home's heat loss, takes off the grant and shows a range. You can add more sizes or change prices later on the Pricing tab.
            </p>
            {priceIssues > 0 && (
              <div className="flash flash-danger">
                {priceIssues === 1 ? "1 size needs attention" : `${priceIssues} sizes need attention`} before you can carry on.
              </div>
            )}
            <PriceEditor state={prices} onChange={setPrices} />
          </section>
          <div className="wiz-actions wiz-actions-end">
            {!pricesReady && priceIssues === 0 && <span className="wiz-wait">Switch on at least one size and give it a price.</span>}
            <span className="has-attention">
              <button type="button" className="btn btn-primary" onClick={() => void savePrices()} disabled={saving || !pricesReady}>
                {saving ? "Saving…" : hostedTool ? "Save and go to branding" : "Save and add websites"} <ArrowRight size={16} aria-hidden="true" />
              </button>
              {pricesReady && !saving && <Attention corner="tr" label="Save and continue" />}
            </span>
          </div>
        </div>
      )}

      {/* Step 6: where the calculator may run (not for hosted pages) */}
      {step === "websites" && widgetId && (
        <div className="wiz">
          <section className="card">
            <h2>Which websites will it go on?</h2>
            <p className="card-sub">
              The calculator only runs on the websites you list here; anywhere else it stays hidden, so nobody can copy it onto their own site.
              <strong> You need at least one.</strong> Adding <code>installer.co.uk</code> also allows <code>www.installer.co.uk</code>. Sites
              must use https. You can add more later on the Install tab.
            </p>
            <DomainsEditor domains={domains} onChange={setDomains} guide />
          </section>
          <div className="wiz-actions wiz-actions-end">
            {domains.length === 0 && <span className="wiz-wait">Add at least one website.</span>}
            <span className="has-attention">
              <button type="button" className="btn btn-primary" onClick={() => void saveDomains()} disabled={saving || domains.length === 0}>
                {saving ? "Saving…" : "Save and go to branding"} <ArrowRight size={16} aria-hidden="true" />
              </button>
              {domains.length > 0 && !saving && <Attention corner="tr" label="Save and continue" />}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
